export const customerModels = Object.freeze(["Pirdy Spark", "Pirdy Engage", "Pirdy Spotlight", "Pirdy Showcase", "Pirdy Impact"]);
const legacyModels = ["Pirdy Uno", "Pirdy Uno Touch", "Pirdy Duos", "Pirdy Duos Touch", "Pirdy 7 inch"];
export const productCatalog = Object.freeze([
  { size: '2.8"', name: "Pirdy Spark", tagline: "Small screen. Big curiosity." },
  { size: '3.5"', name: "Pirdy Engage", tagline: "Turn curiosity into interaction." },
  { size: '5"', name: "Pirdy Spotlight", tagline: "Make your product the focus." },
  { size: '7"', name: "Pirdy Showcase", tagline: "Give your brand room to shine." },
  { size: '10"', name: "Pirdy Impact", tagline: "Make every impression count." }
]);
const key = value => String(value ?? "").trim().toLowerCase();
export function customerModelName(device) {
  const profile = ({
    pirdy_c3_28_touch: "Pirdy Spark", scroller_cyd_28_touch: "Pirdy Spark",
    scroller_cyd_28_st7789_touch: "Pirdy Spark", scroller_cyd_28_st7789_secure: "Pirdy Spark",
    pirdy_c3_28_notouch: "Pirdy Spark", pirdy_c3_35_duos_touch: "Pirdy Engage",
    pirdy_c3_35_duos_notouch: "Pirdy Engage", scroller_cyd_35_touch: "Pirdy Engage",
    pirdy_crowpanel_50_touch: "Pirdy Spotlight",
    pirdy_crowpanel_70_touch: "Pirdy Showcase"
  })[key(device?.profileId ?? device?.hardwareProfile)];
  if (profile) return profile;
  const reported = device?.customerModelName ?? device?.customerName?.modelName ?? device?.modelName;
  return customerModels.find(name => key(name) === key(reported)) ?? ({
    "pirdy uno": "Pirdy Spark", "pirdy uno touch": "Pirdy Spark",
    "pirdy duos": "Pirdy Engage", "pirdy duos touch": "Pirdy Engage", "pirdy 7 inch": "Pirdy Showcase"
  })[key(reported)] ?? "Pirdy";
}

// Presentation-only, account-scoped cache. Never use labels as command targets.
// This is not a Cloud nickname API or cross-computer synchronization contract.
export class CustomerDeviceLabels {
  constructor(storage) { this.storage = storage; this.accounts = new Map(); }
  include(scope, devices) {
    const storageKey = scope ? `pirdy.device-labels.v1:${scope}` : null;
    let labels = this.accounts.get(scope);
    if (!labels) {
      try {
        const saved = storageKey && this.storage?.getItem(storageKey);
        const parsed = saved ? JSON.parse(saved) : [];
        labels = Array.isArray(parsed) && parsed.every(item => item && typeof item.id === "string" && item.id === key(item.id) && item.id && (customerModels.includes(item.model) || legacyModels.includes(item.model) || item.model === "Pirdy") && Number.isSafeInteger(item.sequence) && item.sequence > 0) && new Set(parsed.map(item => item.id)).size === parsed.length && new Set(parsed.map(item => `${item.model}:${item.sequence}`)).size === parsed.length ? parsed : [];
      } catch { labels = []; }
      this.accounts.set(scope, labels);
    }
    let changed = false;
    for (const device of devices) {
      const id = key(device.deviceId);
      if (!id) continue;
      const model = customerModelName(device);
      const prior = labels.findIndex(item => item.id === id);
      if (prior >= 0 && !(labels[prior].model === "Pirdy" && customerModels.includes(model))) continue;
      const sequence = 1 + Math.max(0, ...labels.filter(item => item.model === model).map(item => item.sequence));
      if (prior >= 0) labels[prior] = { id, model, sequence };
      else labels.push({ id, model, sequence });
      changed = true;
    }
    if (changed && storageKey) { try { this.storage?.setItem(storageKey, JSON.stringify(labels)); } catch { /* Blocked storage never blocks device operations. */ } }
    return labels;
  }
  name(device, workspace) {
    const shared = device?.customerName;
    if (shared && key(shared.deviceId) === key(device.deviceId) && Number.isSafeInteger(shared.sequence) && shared.sequence > 0 && Number.isSafeInteger(shared.version) && shared.version > 0) {
      const model = customerModelName(device);
      return shared.nickname?.trim() || (shared.sequence === 1 ? model : `${model} ${shared.sequence}`);
    }
    const me = workspace?.me;
    const scope = me?.organizationId && me?.userId ? `${encodeURIComponent(me.organizationId)}:${encodeURIComponent(me.userId)}` : null;
    const values = Array.isArray(workspace?.devices) ? workspace.devices : workspace?.devices?.items ?? workspace?.devices?.value ?? [];
    const devices = [...values].sort((a, b) => key(a.deviceId).localeCompare(key(b.deviceId)));
    const labels = this.include(scope, devices);
    const label = labels.find(item => item.id === key(device?.deviceId));
    const named = String(device?.nickname ?? device?.name ?? device?.deviceName ?? "").trim();
    const legacy = !named || key(named) === key(device?.deviceId) || /^pirdy[- ]\d+$/i.test(named);
    if (!legacy) return named;
    const model = customerModelName(device);
    return !label || label.sequence === 1 ? model : `${model} ${label.sequence}`;
  }
}

let storage;
try { storage = globalThis.localStorage; } catch { /* Privacy mode: session labels only. */ }
const labels = new CustomerDeviceLabels(storage);
export const customerDeviceName = (device, workspace) => labels.name(device, workspace);
